import { FieldTypeEnum } from '../custom-field.types'
import { ModuleColumnDefinition, ModuleTypeEnum } from '../module'

/**
 * Files / Notes count columns: how many files and notes sit on each record's
 * Files tab. Hidden by default; people turn them on from the table's
 * "Existing" columns. The counts are not part of the list payloads — each
 * visible cell asks `POST /record-counts/attachments` (batched per tick), so
 * a list pays nothing while the columns are off.
 */
export const FILE_COUNT_COLUMN_ID = 'fileCount'
export const NOTE_COUNT_COLUMN_ID = 'noteCount'

export type AttachmentCountColumnId =
  | typeof FILE_COUNT_COLUMN_ID
  | typeof NOTE_COUNT_COLUMN_ID

export function isAttachmentCountColumnId(
  id: string,
): id is AttachmentCountColumnId {
  return id === FILE_COUNT_COLUMN_ID || id === NOTE_COUNT_COLUMN_ID
}

/** Modules whose records have a Files tab with notes and whose readers the
 * counts endpoint can check in bulk. */
export const ATTACHMENT_COUNT_MODULES = [
  ModuleTypeEnum.TASK,
  ModuleTypeEnum.PROJECT,
  ModuleTypeEnum.ENTITY,
  ModuleTypeEnum.DEAL,
  ModuleTypeEnum.SUPPLIER,
] as const

export type AttachmentCountModule = (typeof ATTACHMENT_COUNT_MODULES)[number]

export function isAttachmentCountModule(
  moduleType: string | null | undefined,
): moduleType is AttachmentCountModule {
  return (ATTACHMENT_COUNT_MODULES as readonly string[]).includes(
    moduleType ?? '',
  )
}

/** Most records one counts request may ask about. */
export const MAX_ATTACHMENT_COUNT_RECORDS = 200

export interface RecordAttachmentCountsRequest {
  moduleType: AttachmentCountModule
  recordIds: string[]
}

export interface RecordAttachmentCounts {
  files: number
  notes: number
}

/** Readable records only; ids the viewer cannot read are left out. */
export interface RecordAttachmentCountsResponse {
  counts: Record<string, RecordAttachmentCounts>
}

/** The two hidden columns, appended after a module's own columns. */
export function createAttachmentCountColumns(
  recordLabel: string,
  order: number,
): ModuleColumnDefinition[] {
  const record = recordLabel.toLowerCase()
  return [
    {
      id: FILE_COUNT_COLUMN_ID,
      name: FILE_COUNT_COLUMN_ID,
      label: 'Files',
      description: `Files on this ${record}`,
      isVisible: false,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      isReadOnly: true,
      order,
      fieldType: FieldTypeEnum.enum.NUMBER,
      width: '90px',
      minWidth: '80px',
      icon: 'Paperclip',
    },
    {
      id: NOTE_COUNT_COLUMN_ID,
      name: NOTE_COUNT_COLUMN_ID,
      label: 'Notes',
      description: `Notes on this ${record}`,
      isVisible: false,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      isReadOnly: true,
      order: order + 0.1,
      fieldType: FieldTypeEnum.enum.NUMBER,
      width: '90px',
      minWidth: '80px',
      icon: 'Notebook',
    },
  ]
}
